import pFileReader from 'promise-file-reader';
import unorm from 'unorm';

import { requestApi } from '@app/api';
import { withUpload } from '@app/api/paths';
import {
  GOOGLE_DOCUMENT_MAX_FILE_UPLOAD_BYTE,
  GOOGLE_DOCUMENT_MAX_FILE_UPLOAD_BYTE_ERROR_MESSAGE,
  GOOGLE_DOCUMENT_MIME_TYPE,
  MAX_FILE_UPLOAD_BYTE,
  MAX_FILE_UPLOAD_BYTE_ERROR_MESSAGE,
} from '@app/constants/UpLoadModal';

interface DocumentUploadResult {
  fileId: string;
  url: string;
}

/**
 * POST 문서 업로드
 * @params {object}
 * @params {?string} params.base64 - 로컬파일 업로드시 사용하는 base64 Data
 * @params {?string} params.extension - 파일 확장자
 * @params {?string} params.url - 파일 업로드 url
 * @returns {object}
 * @property {string} fileId - 저장된 파일 id
 * @property {string} url - 저장된 파일 url
 */
export async function postDocumentUpload({
  base64 = null,
  extension: extensionParam = 'pdf',
  url,
}: {
  base64?: string | null;
  extension?: string;
  url?: string;
}): Promise<DocumentUploadResult> {
  const extension = `.${extensionParam}`;
  const data = base64 ? { base64, ext: extension } : { ext: extension, url };

  const response = await requestApi<DocumentUploadResult>({
    url: withUpload('/documents/upload'),
    method: 'POST',
    data,
    credentials: true,
  });

  return response.data;
}

interface UploadFile {
  accessToken?: string;
  size: number;
  downloadLink: string;
  mimeType?: string;
  name: string;
}

interface BlobFile {
  name: string;
  blob: Blob;
}

interface SampleFile {
  name: string;
  url: string;
}

/**
 * 문서 파일 업로드(내문서, 편집 문서)
 * @param {object} file - 파일 객체
 * @returns {object}
 * @property {boolean} isThirdParty - 써드파티 파일 여부
 * @property {string} fileId - 업로드된 파일 id
 * @property {string} title - 문서제목(파일명)
 * @property {string} url - 업로드된 파일 url
 */
export async function documentUpload(file: File | SampleFile | UploadFile | BlobFile): Promise<{
  isThirdParty: boolean;
  fileId: string;
  title: string;
  url: string;
}> {
  const parsing: string[] = file.name.split('.');
  let extension = '';
  if (parsing.length > 1) {
    const poppedExtension = parsing.pop();
    if (typeof poppedExtension === 'string') {
      extension = poppedExtension.toLowerCase();
    }
  }
  const title = unorm.nfc(parsing.join('.'));

  let fileUploadParams;
  let isThirdParty = false;
  if ('downloadLink' in file) {
    // downloadLink 속성이 있으면 UploadFile 타입
    isThirdParty = true;
    fileUploadParams = { extension, url: file.downloadLink };
    // Google Drive를 통해 파일을 가져왔을 때 access token을 query string으로
    // 넘기는 것은 허용되지 않으므로 클라이언트에서 파일을 불러온 뒤 직접 파일을 전송합니다.
    // https://github.com/modusign/modusign-client/issues/2240
    if (
      file?.accessToken &&
      typeof file?.downloadLink === 'string' &&
      typeof file?.mimeType === 'string'
    ) {
      const response = await requestApi<Blob>({
        url: file.downloadLink,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${file.accessToken}`,
        },
        credentials: false,
        responseType: 'blob',
      });
      const fileData = await pFileReader.readAsDataURL(response.data);
      const base64 = fileData.split(',')[1];

      /**
       * 구글 문서는 pdf로 다운받을 경우 기본 용량이 5MB를 넘습니다.
       * 이 문제를 해결하기위해 docx로 다운로드 후 업로드하면 pdf 변환 과정에서
       * 문서가 의도한 대로 변환되지 않는 문제가 있었습니다.
       *
       * 임시적인 조치로 구글 문서에 한해 업로드 제한 용량을 8MB로 하고
       * 나머지는 5MB를 유지하게 수정합니다.
       * @see: https://modusign.slack.com/archives/C01GRPG5BJR/p1645582114735839?thread_ts=1645579283.036769&cid=C01GRPG5BJR
       */
      const isGoogleDocument = file.mimeType === GOOGLE_DOCUMENT_MIME_TYPE;
      const maxFileSize = isGoogleDocument
        ? GOOGLE_DOCUMENT_MAX_FILE_UPLOAD_BYTE
        : MAX_FILE_UPLOAD_BYTE;

      if (fileData.length > maxFileSize) {
        throw new Error(
          isGoogleDocument
            ? GOOGLE_DOCUMENT_MAX_FILE_UPLOAD_BYTE_ERROR_MESSAGE
            : MAX_FILE_UPLOAD_BYTE_ERROR_MESSAGE
        );
      }
      fileUploadParams = { base64, extension };
    }
  } else if ('url' in file) {
    // url 속성이 있으면 SampleFile 타입
    fileUploadParams = { extension, url: file.url };
  } else if ('blob' in file) {
    // blob 속성이 있으면 문서작성(초안)으로 생성한 파일타입
    const fileData = await pFileReader.readAsDataURL(file.blob);
    const base64 = fileData.split(',')[1];
    fileUploadParams = { extension, base64 };
  } else {
    // File 타입
    const fileData = await pFileReader.readAsDataURL(file);
    const base64 = fileData.split(',')[1];
    fileUploadParams = { base64, extension };
  }

  if (fileUploadParams) {
    const { fileId, url } = await postDocumentUpload(fileUploadParams);
    return {
      isThirdParty,
      fileId,
      title,
      url,
    };
  }
  throw new Error('문서 업로드 실패');
}
